import React, { useEffect, useState } from "react";
import { Button, Typography, Box, LinearProgress, linearProgressClasses, useMediaQuery } from "@mui/material";
import { fetchImage, getImagePreview, loadImageFromCache, useCheckout, useGeneratedImage, useImageAPIError, useImagePreview, useIsLoading } from "../../state/image";
import { useDispatch } from "react-redux";
import { Link, useParams } from "react-router-dom";
import Title from "../title";
import { STATIC_FILE_URL } from "../../constants";
import { GradientText } from "../dashboard/Gradient";
import styled from "styled-components";

const BorderLinearProgress = styled(LinearProgress)`
  height: 10,
  borderRadius: 0,
  &.${linearProgressClasses.colorPrimary}: {
    backgroundColor: red,
  },
  & .${linearProgressClasses.bar}: {
    borderRadius: 5,
    backgroundColor: '#1a90ff',
  }
`;

const Preview = () => {
  const { token } = useParams<{ token: string | undefined }>();
  const dispatch = useDispatch();
  const imagePreview = useImagePreview();
  const checkoutURL = useCheckout();
  const error = useImageAPIError();
  const loading = useIsLoading();
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (imagePreview && !isComplete) {
      setTimeout(() => setIsComplete(true), 2000);
    }
  }, [imagePreview, setIsComplete])

  useEffect(() => {
    if (token && typeof token == "string") {
      dispatch(getImagePreview(token))
    }
  }, [dispatch])

  if (!token || typeof token !== "string") {
    return null;
  }
  if (!imagePreview) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center">
        <Typography variant="h2">
          Creating your <GradientText variant="h2" display="inline">preview</GradientText>
        </Typography>
        <Typography variant="subtitle1" mb="2rem" fontSize="0.75rem">
          Your map may include more detail than what's visible in the editor, such as extra streets and landmarks, due to its higher resolution.
        </Typography>
        <LoadingBar isComplete={isComplete} />
      </Box>
    )
  }
  return (
    <Box display="flex" flexDirection="column" alignItems="center">
       <Typography variant="h2">
          Your <GradientText variant="h2" display="inline">preview</GradientText>
        </Typography>
      <Box>
        <Typography variant="subtitle1" mb="2rem" fontSize="0.75rem">
          Your map may include more detail than what's visible in the editor, such as extra streets and landmarks, due to its higher resolution.
        </Typography>
      </Box>
      {!loading && error && <Box my="2rem">
        <Typography color="error" variant="subtitle1">
          {error || "Unknown Error when creating image"}
        </Typography>
      </Box>}
      <Box>
        <img alt="created-map-preview" src={imagePreview} style={{ maxWidth: "40vw", maxHeight: "40vh" }}/>
      </Box>
      <Box my="32px" display="flex" gap="16px" flexDirection={"column"}>
        {checkoutURL 
          ? <Button
              // startIcon={<HighQualityIcon/>}
              onClick={() => window.location.assign(checkoutURL)}
              variant="contained"
              // color="secondary"
              sx={{ textTransform: "none" }}
            >
              Preview looks good, create my high-resolution map
            </Button>
          : <Button
              component={Link} 
              to={`/map/${token}`} 
              variant="contained" 
              // color="secondary" 
              sx={{ textTransform: "none"}}
            >
              View high quality map
            </Button>
        }
        <Button
          component={Link} 
          to={`/create`} 
          variant="outlined" 
          color="secondary" 
          sx={{ textTransform: "none", mr: "16px" }}
        >
          Go back
        </Button>
      </Box>
      <Typography variant="body1" color="primary" sx={{ my: "16px" }}>
        If you have any issues with your image preview, please contact {" "}
        <a style={{color: "white" }} href={"mailto:help@mappin.pro"}>help@mappin.pro</a>.
      </Typography>
    </Box>
  );
};

export const LoadingBar = ({ isComplete }: { isComplete?: boolean }) => {
  const [progress, setProgress] = useState<number>(isComplete ? 100 : 0);
  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((prevProgress) => {
        if (prevProgress < 30) {
          return prevProgress + 2.5;
        } else if (prevProgress < 70) {
          return prevProgress + 0.5;
        } else if (prevProgress >= 100) {
          return 100;
        } else if (prevProgress > 95) {
          return prevProgress + 0.025;
        }
        return prevProgress + 0.1;
      });
    }, 500);
    return () => {
      clearInterval(timer);
    };
  }, [setProgress]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box sx={{ width: '300px', mr: "0.5rem" }}>
        <BorderLinearProgress variant="determinate" value={progress} />
      </Box>
      <Box sx={{ minWidth: 35 }}>
        <Typography
          variant="body2"
          sx={{ color: isComplete ? undefined : 'text.secondary' }}
        >{`${Math.round(progress)}%`}</Typography>
      </Box>
    </Box>
  );
}

export default Preview;
